import {
  Alert,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { ENTITY_CATEGORIES } from "../../utils/entity/categories";
import { useLocalState } from "../../utils/hooks/global/useLocalStorage";
import CustomButton from "../global/CustomButton";

const initExchangeFormFields = {
  name: "",
  hasNativeAssets: false,
  trustScore: "",
};

const initWalletFormFields = {
  name: "",
  category: "",
  hasNativeAssets: false,
  hasStaking: false,
  trustScore: "",
};

const TRUST_SCORES = [
  { value: "green", label: "Green" },
  { value: "yellow", label: "Yellow" },
  { value: "red", label: "Red" },
];

const CreateForm = ({ onSubmit, initDataWallet, initDataExchange }) => {
  const [jwt] = useLocalState(null, "jwt");
  const [errorMessage, setErrorMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [formType, setFormType] = useState("");
  const [formData, setFormData] = useState({
    exchange: initDataExchange
      ? { ...initDataExchange }
      : initExchangeFormFields,
    wallet: initDataWallet ? { ...initDataWallet } : initWalletFormFields,
  });
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const fields = formType ? formData[formType] : null;

  const setField = (name, value) => {
    setErrorMessage("");
    setFormData((prevData) => ({
      ...prevData,
      [formType]: {
        ...prevData[formType],
        [name]: value,
      },
    }));
  };

  const canCreate =
    !!fields &&
    !!fields.name.trim() &&
    !!fields.trustScore &&
    (formType !== "wallet" || !!fields.category);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!canCreate || saving) return;
    setSaving(true);
    try {
      const response = await fetch(`${backendUrl}/entities/`, {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + jwt,
        },
        body: JSON.stringify({
          name: fields.name.trim(),
          type: formType,
          [formType]: { ...fields, name: fields.name.trim() },
        }),
      });
      if (!response.ok) {
        throw new Error(
          response.status === 409
            ? "Entity already exists!"
            : "There was an error processing your request."
        );
      }
      const data = await response.json();
      setErrorMessage("");
      onSubmit(data);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack component="form" spacing={2} onSubmit={handleSubmit}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        New entity
      </Typography>
      <FormControl fullWidth>
        <InputLabel id="entity-type-label">Type</InputLabel>
        <Select
          labelId="entity-type-label"
          id="entity-type"
          value={formType}
          label="Type"
          onChange={(e) => {
            setErrorMessage("");
            setFormType(e.target.value);
          }}
        >
          <MenuItem value="exchange">Exchange</MenuItem>
          <MenuItem value="wallet">Other entity (wallet, custodian, …)</MenuItem>
        </Select>
      </FormControl>

      {fields && (
        <>
          <TextField
            label="Name"
            value={fields.name}
            onChange={(e) => setField("name", e.target.value)}
            required
            fullWidth
          />
          {formType === "wallet" && (
            <FormControl fullWidth required>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                value={fields.category}
                label="Category"
                onChange={(e) => setField("category", e.target.value)}
              >
                {ENTITY_CATEGORIES.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth required>
            <InputLabel id="trust-score-label">Trust score</InputLabel>
            <Select
              labelId="trust-score-label"
              value={fields.trustScore}
              label="Trust score"
              onChange={(e) => setField("trustScore", e.target.value)}
            >
              {TRUST_SCORES.map((score) => (
                <MenuItem key={score.value} value={score.value}>
                  {score.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" sx={{ flexWrap: "wrap", columnGap: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!fields.hasNativeAssets}
                  onChange={(e) =>
                    setField("hasNativeAssets", e.target.checked)
                  }
                />
              }
              label="Native assets"
            />
            {formType === "wallet" && (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!fields.hasStaking}
                    onChange={(e) => setField("hasStaking", e.target.checked)}
                  />
                }
                label="Staking"
              />
            )}
          </Stack>
        </>
      )}

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      <Stack direction="row" justifyContent="flex-end">
        <CustomButton
          onClick={handleSubmit}
          disabled={!canCreate || saving}
          buttonText={saving ? "Creating…" : "Create"}
          color={"black"}
        />
      </Stack>
    </Stack>
  );
};

export default CreateForm;
